import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, CheckCircle2, Circle, Trash2, Sparkles, BarChart3,
  Flame, Compass, ListChecks, Sunrise, LogOut, Info, HelpCircle, Calendar as CalendarIcon,
  Archive
} from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import AIChat from '@/components/AIChat';
import ModeToggle from '@/components/ModeToggle';
import WeeklyReviewCard from '@/components/WeeklyReviewCard';
import StalledGoalsCard from '@/components/StalledGoalsCard';
import ArchivedGoalsDialog from '@/components/ArchivedGoalsDialog';
import CalendarView from '@/components/CalendarView';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ token, user, setToken }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [beings, setBeings] = useState([]);
  const [wants, setWants] = useState([]);
  const [todos, setTodos] = useState([]);
  const [todayAnchors, setTodayAnchors] = useState([]);
  const [stalledWants, setStalledWants] = useState([]);
  const [showBeingForm, setShowBeingForm] = useState(false);
  const [showWantForm, setShowWantForm] = useState(false);
  const [showTodoForm, setShowTodoForm] = useState(false);
  const [showArchivedDialog, setShowArchivedDialog] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'being' | 'want', id: string }

  const [beingForm, setBeingForm] = useState({ title: '', description: '', type: 'identity' });
  const [wantForm, setWantForm] = useState({ title: '', description: '', being_id: '' });
  const [todoForm, setTodoForm] = useState({ title: '', description: '', want_id: '', being_id: '' });

  // Hover-driven focus chain. When hovering a card, its linked ancestors/descendants light up.
  const [focus, setFocus] = useState(null); // { beingId?, wantId?, todoId? }

  // Move 2: when the user engages with the weekly review, we seed the AI chat
  // with the review's prompt as an assistant turn so they can reply directly.
  const [pendingChatPrompt, setPendingChatPrompt] = useState(null);
  const [isReviewDue, setIsReviewDue] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'calendar'

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const anchorByItemId = useMemo(() => {
    return Object.fromEntries(todayAnchors.map(a => [a.item_id, a]));
  }, [todayAnchors]);

  const fetchData = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      const [beingsRes, wantsRes, todosRes, anchorsRes, stalledRes] = await Promise.all([
        axios.get(`${API}/beings`, config),
        axios.get(`${API}/wants`, config),
        axios.get(`${API}/todos`, config),
        axios.get(`${API}/calendar/anchors`, { ...config, params: { date: todayStr } }),
        axios.get(`${API}/wants/stalled`, config).catch(() => ({ data: [] }))
      ]);
      setBeings(beingsRes.data);
      setWants(wantsRes.data);
      setTodos(todosRes.data);
      setTodayAnchors(anchorsRes.data);
      setStalledWants(stalledRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('toast.session.expired'));
        setToken(null);
        navigate('/');
      }
    }
  }, [token, t, setToken, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startTour = useCallback(() => {
    setTourActive(true);
    setTimeout(() => {
      const steps = viewMode === 'calendar' ? [
        {
          element: '[data-testid="calendar-view-container"]',
          popover: {
            title: t('tour.calendar.step1.title', 'Day Planner Cockpit'),
            description: t('tour.calendar.step1.desc', 'Welcome to your calendar. Here you can organize your daily activities in a visual timeline.'),
            side: "bottom",
            align: "center"
          }
        },
        {
          element: '[data-testid="calendar-datepicker-input"]',
          popover: {
            title: t('tour.calendar.step2.title', 'Date Navigation'),
            description: t('tour.calendar.step2.desc', 'Move between days to schedule tasks in advance or review how you spent past days.'),
            side: "bottom",
            align: "end"
          }
        },
        {
          element: '[data-testid="calendar-unscheduled-sidebar"]',
          popover: {
            title: t('tour.calendar.step3.title', 'Unscheduled Items'),
            description: t('tour.calendar.step3.desc', "This panel holds all your active Beings, Wants, and Todos that have not yet been placed on today's schedule."),
            side: "right",
            align: "start"
          }
        },
        {
          element: '[data-testid="calendar-timeline-scroll"]',
          popover: {
            title: t('tour.calendar.step4.title', 'Hourly Timeline'),
            description: t('tour.calendar.step4.desc', 'Click "+ Add block to this time" to pick a task from your unscheduled list and block out time for it.'),
            side: "left",
            align: "start"
          }
        },
        {
          element: '[data-testid="calendar-toggle-btn"]',
          popover: {
            title: t('tour.calendar.step5.title', 'Switch View Mode'),
            description: t('tour.calendar.step5.desc', 'Click this button to switch back and forth between the Calendar and your three-column Dashboard.'),
            side: "bottom",
            align: "end"
          }
        }
      ] : [
        {
          popover: {
            title: t('tour.step1.title'),
            description: t('tour.step1.desc'),
            side: "left",
            align: "start"
          }
        },
        {
          element: '[data-testid="being-column-header"]',
          popover: {
            title: t('tour.step2.title'),
            description: t('tour.step2.desc'),
            side: "right",
            align: "start"
          }
        },
        {
          element: '[data-testid="want-column-header"]',
          popover: {
            title: t('tour.step3.title'),
            description: t('tour.step3.desc'),
            side: "right",
            align: "start"
          }
        },
        {
          element: '[data-testid="todo-column-header"]',
          popover: {
            title: t('tour.step4.title'),
            description: t('tour.step4.desc'),
            side: "left",
            align: "start"
          }
        },
        {
          element: '[data-testid="today-strip"]',
          popover: {
            title: t('tour.step5.title'),
            description: t('tour.step5.desc'),
            side: "left",
            align: "start"
          }
        },
        {
          element: '[data-testid="ai-chat-fab"]',
          popover: {
            title: t('tour.step6.title'),
            description: t('tour.step6.desc'),
            side: "left",
            align: "start"
          }
        },
        {
          element: '[data-testid="header-nav-links"]',
          popover: {
            title: t('tour.step7.title'),
            description: t('tour.step7.desc'),
            side: "bottom",
            align: "start"
          }
        },
        ...(document.querySelector('[data-testid="stalled-goal-card"]') ? [{
          element: '[data-testid="stalled-goal-card"]',
          popover: {
            title: t('tour.step_stalled.title'),
            description: user?.subscription?.tier === 'starter'
              ? t('tour.step_stalled.desc_starter')
              : t('tour.step_stalled.desc_pro'),
            side: "left",
            align: "start"
          }
        }] : [])
      ];

      const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayColor: '#000',
        overlayOpacity: 0.5,
        nextBtnText: t('tour.btn.next'),
        prevBtnText: t('tour.btn.prev'),
        doneBtnText: t('tour.btn.done'),
        onDestroyed: () => {
          setTourActive(false);
        },
        steps
      });
      driverObj.drive();
    }, 150);
  }, [t, viewMode, user?.subscription?.tier]);

  useEffect(() => {
    if (user?.id) {
      const tourSeen = localStorage.getItem(`tour_seen_${user.id}`);
      if (!tourSeen) {
        localStorage.setItem(`tour_seen_${user.id}`, 'true');
        startTour();
      }
    }
  }, [user, startTour]);

  const createBeing = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/beings`, beingForm, axiosConfig);
      toast.success(t('toast.being.created'));
      setShowBeingForm(false);
      setBeingForm({ title: '', description: '', type: 'identity' });
      fetchData();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'upgrade_required') {
        toast.error('Upgrade required: Starter plan is limited to 5 Beings. Please upgrade to Pro/Elite.');
      } else {
        toast.error(t('toast.being.error'));
      }
    }
  };

  const createWant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wants`, wantForm, axiosConfig);
      toast.success(t('toast.want.created'));
      setShowWantForm(false);
      setWantForm({ title: '', description: '', being_id: '' });
      fetchData();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'upgrade_required') {
        toast.error('Upgrade required: Starter plan is limited to 10 Wants. Please upgrade to Pro/Elite.');
      } else {
        toast.error(t('toast.want.error'));
      }
    }
  };

  const createTodo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/todos`, todoForm, axiosConfig);
      toast.success(t('toast.todo.created'));
      setShowTodoForm(false);
      setTodoForm({ title: '', description: '', want_id: '', being_id: '' });
      fetchData();
    } catch { toast.error(t('toast.todo.error')); }
  };

  const toggleTodo = async (id) => {
    try {
      await axios.put(`${API}/todos/${id}/toggle`, {}, axiosConfig);
      fetchData();
    } catch { toast.error(t('toast.todo.error')); }
  };

  const practiceBeing = async (id) => {
    try {
      const response = await axios.post(`${API}/beings/${id}/practice`, {}, axiosConfig);
      toast.success(t('toast.streak', { count: response.data.streak }));
      fetchData();
    } catch { toast.error(t('toast.being.error')); }
  };

  const deleteTodo = async (id) => {
    const todoToRestore = todos.find(t => t.id === id);
    try {
      await axios.delete(`${API}/todos/${id}`, axiosConfig);
      fetchData();
      if (todoToRestore) {
        toast.success(t('toast.todo.deleted', 'Task deleted'), {
          action: {
            label: t('dialog.delete.undo', 'Undo'),
            onClick: async () => {
              try {
                await axios.post(`${API}/todos`, {
                  title: todoToRestore.title,
                  description: todoToRestore.description,
                  want_id: todoToRestore.want_id,
                  being_id: todoToRestore.being_id,
                  due_date: todoToRestore.due_date
                }, axiosConfig);
                toast.success(t('toast.todo.restored', 'Task restored'));
                fetchData();
              } catch {
                toast.error(t('toast.todo.restore_error', 'Could not restore task'));
              }
            }
          }
        });
      } else {
        toast.success(t('toast.todo.deleted', 'Task deleted'));
      }
    } catch { toast.error(t('toast.todo.error')); }
  };

  const deleteWant = async (id) => {
    try {
      await axios.delete(`${API}/wants/${id}`, axiosConfig);
      toast.success(t('toast.want.deleted'));
      fetchData();
    } catch { toast.error(t('toast.want.error')); }
  };

  const deleteBeing = async (id) => {
    try {
      await axios.delete(`${API}/beings/${id}`, axiosConfig);
      toast.success(t('toast.being.deleted'));
      fetchData();
    } catch { toast.error(t('toast.being.error')); }
  };

  const handleDeleteWantClick = (id) => {
    setDeleteTarget({ type: 'want', id });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteBeingClick = (id) => {
    setDeleteTarget({ type: 'being', id });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTodoClick = (id) => {
    setDeleteTarget({ type: 'todo', id });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'being') {
      await deleteBeing(deleteTarget.id);
    } else if (deleteTarget.type === 'want') {
      await deleteWant(deleteTarget.id);
    } else if (deleteTarget.type === 'todo') {
      await deleteTodo(deleteTarget.id);
    }
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  const handleLogout = () => { setToken(null); navigate('/'); };

  const beingById = useMemo(() => Object.fromEntries(beings.map(b => [b.id, b])), [beings]);
  const wantById = useMemo(() => Object.fromEntries(wants.map(w => [w.id, w])), [wants]);

  // === Focus chain logic ===
  // Given a hover target, compute every (kind, id) that should be lit.
  const focusChain = useMemo(() => {
    if (!focus) return null;
    const lit = { beings: new Set(), wants: new Set(), todos: new Set() };
    if (focus.beingId) {
      lit.beings.add(focus.beingId);
      wants.filter(w => w.being_id === focus.beingId).forEach(w => {
        lit.wants.add(w.id);
        todos.filter(td => td.want_id === w.id).forEach(td => lit.todos.add(td.id));
      });
      todos.filter(td => td.being_id === focus.beingId).forEach(td => lit.todos.add(td.id));
    }
    if (focus.wantId) {
      lit.wants.add(focus.wantId);
      const w = wantById[focus.wantId];
      if (w?.being_id) lit.beings.add(w.being_id);
      todos.filter(td => td.want_id === focus.wantId).forEach(td => lit.todos.add(td.id));
    }
    if (focus.todoId) {
      lit.todos.add(focus.todoId);
      const td = todos.find(x => x.id === focus.todoId);
      if (td?.want_id) {
        lit.wants.add(td.want_id);
        const w = wantById[td.want_id];
        if (w?.being_id) lit.beings.add(w.being_id);
      }
      if (td?.being_id) lit.beings.add(td.being_id);
    }
    return lit;
  }, [focus, wants, todos, wantById]);

  const isLit = (kind, id) => focusChain?.[kind]?.has(id);
  const focusClass = (kind, id) => {
    if (!focusChain) return 'focus-target';
    return `focus-target ${isLit(kind, id) ? 'focus-active' : ''}`;
  };

  // === Today strip: focus being + top 3 incomplete todos ===
  const focusBeing = useMemo(() => {
    // pick the habit with the highest streak; fall back to most recent identity
    const habits = beings.filter(b => b.type === 'habit').sort((a, b) => (b.streak || 0) - (a.streak || 0));
    if (habits[0]) return habits[0];
    return beings[0];
  }, [beings]);
  const todayTodos = useMemo(() => {
    const incomplete = todos.filter(td => !td.completed);
    const withDue = incomplete.filter(td => td.due_date).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    const withoutDue = incomplete.filter(td => !td.due_date);
    return [...withDue, ...withoutDue].slice(0, 3);
  }, [todos]);

  const showSidebar = isReviewDue || !!focusBeing || todayTodos.length > 0 || tourActive || stalledWants.length > 0;

  const renderTrialBanner = () => {
    if (user?.subscription?.status !== 'trialing' || !user?.subscription?.trial_end) return null;
    
    const trialEnd = new Date(user.subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Only show during the last 3 days
    if (diffDays <= 0 || diffDays > 3) return null;
    
    const isLastDay = diffTime <= 24 * 60 * 60 * 1000;
    
    if (isLastDay) {
      const hoursLeft = Math.max(1, Math.round(diffTime / (1000 * 60 * 60)));
      return (
        <div className="mb-6 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive flex items-center justify-between text-xs sm:text-sm animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <span className="text-base">⌛</span>
            <span>Your Pro Trial expires in <strong>{hoursLeft} {hoursLeft === 1 ? 'hour' : 'hours'}</strong>. Complete checkout to secure uninterrupted access.</span>
          </div>
          <Button onClick={() => navigate('/checkout')} size="sm" variant="destructive" className="rounded-full text-xs font-semibold px-4 h-7">
            Upgrade Now
          </Button>
        </div>
      );
    } else {
      return (
        <div className="mb-6 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-500 flex items-center justify-between text-xs sm:text-sm animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <span className="text-base">⚠️</span>
            <span>Only <strong>{diffDays} {diffDays === 1 ? 'day' : 'days'}</strong> left of your Pro Trial. Upgrade now to preserve your habit streaks!</span>
          </div>
          <Button onClick={() => navigate('/checkout')} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white rounded-full text-xs font-semibold px-4 h-7 border-none">
            Upgrade Now
          </Button>
        </div>
      );
    }
  };

  const renderHeaderTrialBadge = () => {
    if (user?.subscription?.status !== 'trialing' || !user?.subscription?.trial_end) return null;
    
    const trialEnd = new Date(user.subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return null;
    
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border border-emerald-500/20 whitespace-nowrap flex-shrink-0" data-testid="header-trial-badge">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Pro Trial ({diffDays}d left)
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-2.5 sm:py-4 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center justify-between md:justify-start gap-4 sm:gap-6 w-full md:w-auto">
            <h1 className="font-serif text-xl sm:text-3xl font-light tracking-tight flex-shrink-0" data-testid="dashboard-title">{t('landing.title')}</h1>
            <div className="flex items-center gap-2 sm:gap-3">
              <ModeToggle active="personal" user={user} />
              {renderHeaderTrialBadge()}
            </div>
          </div>
          <div className="flex items-center justify-start gap-1 sm:gap-3 w-full md:w-auto md:overflow-visible overflow-x-auto py-1">
            <div className="flex items-center gap-1 sm:gap-3" data-testid="header-nav-links">
              <Button onClick={() => setViewMode(v => v === 'dashboard' ? 'calendar' : 'dashboard')} variant="ghost" size="icon" className={`rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto ${viewMode === 'calendar' ? 'bg-secondary/60 text-foreground' : ''}`} data-testid="calendar-toggle-btn" title={viewMode === 'calendar' ? t('nav.dashboard') : t('nav.calendar')} aria-label={viewMode === 'calendar' ? t('nav.dashboard') : t('nav.calendar')}>
                <CalendarIcon className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{viewMode === 'calendar' ? t('nav.dashboard') : t('nav.calendar')}</span>
              </Button>
              <Button onClick={() => navigate('/reflect')} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto" data-testid="reflect-nav-btn" title={t('nav.reflect')} aria-label={t('nav.reflect')}>
                <Sparkles className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('nav.reflect')}</span>
              </Button>
              <Button onClick={() => navigate('/analytics')} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto" data-testid="analytics-nav-btn" title={t('nav.progress')} aria-label={t('nav.progress')}>
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('nav.progress')}</span>
              </Button>
            </div>
            <Button onClick={() => navigate('/about')} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto" data-testid="about-nav-btn" title={t('nav.about')} aria-label={t('nav.about')}>
              <Info className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('nav.about')}</span>
            </Button>
            <Button onClick={startTour} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto text-accent hover:text-accent-foreground" data-testid="help-btn" title={t('tour.helpTooltip')} aria-label={t('tour.helpTooltip')}>
              <HelpCircle className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('nav.help')}</span>
            </Button>
            <LanguageSwitcher />
            <Button onClick={handleLogout} variant="ghost" size="icon" className="rounded-full sm:size-auto sm:px-3 text-xs sm:text-sm h-8 w-8 sm:w-auto" data-testid="logout-btn" title={t('nav.logout')} aria-label={t('nav.logout')}>
              <LogOut className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className={`max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10 pb-24 sm:pb-24 ${focus ? 'dashboard-focused' : ''}`}>
        {renderTrialBanner()}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8 items-start">
          {/* Left side: Main columns */}
          <div className={showSidebar ? "lg:col-span-3" : "lg:col-span-4"}>
            {viewMode === 'calendar' ? (
              <div className="relative animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out">
                <CalendarView
                  token={token}
                  beings={beings}
                  wants={wants}
                  todos={todos}
                  onRefreshData={fetchData}
                />
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10">

          {/* ============ TO BE ============ */}
          <section className="space-y-4 sm:space-y-5 fade-in layer-be" data-testid="being-column">
            <ColumnHeader
              title={`${t('layer.being')}${user?.subscription?.tier === 'starter' ? ` (${beings.length}/5)` : ''}`}
              subtitle={t('layer.being.subtitle')}
              fontClass="font-sans font-medium"
              onAdd={() => setShowBeingForm(v => !v)}
              addTestId="add-being-btn"
              headerTestId="being-column-header"
            />

            {showBeingForm && (
              <form onSubmit={createBeing} className="bg-secondary/30 rounded-xl p-4 sm:p-5 space-y-3 border border-border/60" data-testid="being-form">
                <Input placeholder={t('being.title.placeholder')} value={beingForm.title} onChange={(e) => setBeingForm({ ...beingForm, title: e.target.value })} className="text-base" required data-testid="being-title-input" />
                <Textarea placeholder={t('being.description.placeholder')} value={beingForm.description} onChange={(e) => setBeingForm({ ...beingForm, description: e.target.value })} required className="resize-none text-sm" rows={3} data-testid="being-description-input" />
                <select value={beingForm.type} onChange={(e) => setBeingForm({ ...beingForm, type: e.target.value })} className="w-full p-2 rounded-lg bg-background border border-border text-xs" data-testid="being-type-select">
                  <option value="identity">{t('being.type.identity')}</option>
                  <option value="value">{t('being.type.value')}</option>
                  <option value="habit">{t('being.type.habit')}</option>
                </select>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="rounded-full text-xs" data-testid="being-submit-btn">{t('form.create')}</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowBeingForm(false)} data-testid="being-cancel-btn">{t('form.cancel')}</Button>
                </div>
              </form>
            )}

            <div className="space-y-3 sm:space-y-4 layer-column">
              {beings.length === 0 ? (
                <EmptyState icon={<Compass className="w-6 h-6" />} text={t('being.empty')} />
              ) : (
                beings.map((being) => (
                  <article
                    key={being.id}
                    className={`card-be group rounded-xl p-5 sm:p-6 hover:shadow-md transition-all duration-300 ${focusClass('beings', being.id)}`}
                    onMouseEnter={() => setFocus({ beingId: being.id })}
                    onMouseLeave={() => setFocus(null)}
                    data-testid={`being-item-${being.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0 pl-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t(`being.type.${being.type}`)}</span>
                          {anchorByItemId[being.id] && (
                            <span className="bg-accent/15 text-accent border border-accent/20 px-2 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1 font-sans font-semibold">
                              📅 {anchorByItemId[being.id].time}
                            </span>
                          )}
                        </div>
                        <h3 className="font-sans text-base sm:text-lg font-semibold mt-1 leading-snug">{being.title}</h3>
                      </div>
                      <button onClick={() => handleDeleteBeingClick(being.id)} className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100" data-testid={`being-delete-${being.id}`}>
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed pl-3 mt-3">{being.description}</p>
                    {being.type === 'habit' && (
                      <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-border/40 pl-3">
                        <div className="streak-flame">
                          <Flame className="w-4 h-4" />
                          <span className="font-semibold text-base">{being.streak || 0}</span>
                          <span className="text-xs text-muted-foreground">{t('being.days')}</span>
                        </div>
                        <Button onClick={() => practiceBeing(being.id)} size="sm" variant="outline" className="rounded-full text-xs h-7" data-testid={`being-practice-${being.id}`}>
                          {t('being.practice')}
                        </Button>
                      </div>
                    )}
                  </article>
                ))
              )}
            </div>
          </section>

          {/* ============ TO WANT ============ */}
          <section className="space-y-4 sm:space-y-5 fade-in layer-want" data-testid="want-column">
            <ColumnHeader
              title={`${t('layer.want')}${user?.subscription?.tier === 'starter' ? ` (${wants.length}/10)` : ''}`}
              subtitle={t('layer.want.subtitle')}
              fontClass="font-sans font-medium"
              onAdd={() => setShowWantForm(v => !v)}
              addTestId="add-want-btn"
              headerTestId="want-column-header"
            />

            {showWantForm && (
              <form onSubmit={createWant} className="bg-secondary/30 rounded-xl p-4 sm:p-5 space-y-3 border border-border/60" data-testid="want-form">
                <Input placeholder={t('want.title.placeholder')} value={wantForm.title} onChange={(e) => setWantForm({ ...wantForm, title: e.target.value })} required className="text-sm" data-testid="want-title-input" />
                <Textarea placeholder={t('want.description.placeholder')} value={wantForm.description} onChange={(e) => setWantForm({ ...wantForm, description: e.target.value })} required className="resize-none text-sm" rows={3} data-testid="want-description-input" />
                <select value={wantForm.being_id} onChange={(e) => setWantForm({ ...wantForm, being_id: e.target.value })} className="w-full p-2 rounded-lg bg-background border border-border text-xs" data-testid="want-being-select">
                  <option value="">{t('todo.connect.being')}</option>
                  {beings.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                </select>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="rounded-full text-xs" data-testid="want-submit-btn">{t('form.create')}</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowWantForm(false)} data-testid="want-cancel-btn">{t('form.cancel')}</Button>
                </div>
              </form>
            )}

            <div className="space-y-3 sm:space-y-4 layer-column">
              {wants.length === 0 ? (
                <EmptyState icon={<Sparkles className="w-6 h-6" />} text={t('want.empty')} />
              ) : (
                wants.map((want) => {
                  const linkedBeing = want.being_id ? beingById[want.being_id] : null;
                  return (
                    <article
                      key={want.id}
                      className={`card-want rounded-xl p-5 hover:shadow-md transition-all duration-300 ${focusClass('wants', want.id)}`}
                      onMouseEnter={() => setFocus({ wantId: want.id })}
                      onMouseLeave={() => setFocus(null)}
                      data-testid={`want-item-${want.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <h3 className="font-semibold text-base sm:text-lg leading-snug">{want.title}</h3>
                          {anchorByItemId[want.id] && (
                            <span className="bg-accent/15 text-accent border border-accent/20 px-2 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1 font-sans font-semibold">
                              📅 {anchorByItemId[want.id].time}
                            </span>
                          )}
                        </div>
                        <button onClick={() => handleDeleteWantClick(want.id)} className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0" data-testid={`want-delete-${want.id}`}>
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3">{want.description}</p>
                      {linkedBeing && (
                        <span className="text-xs px-2 py-1 bg-primary/5 text-primary/80 rounded-full inline-flex items-center gap-1 mb-3">
                          <span className="font-serif italic">⟡</span> {linkedBeing.title}
                        </span>
                      )}
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-baseline text-xs">
                          <span className="text-muted-foreground uppercase tracking-wider">{t('want.progress')}</span>
                          <span className="font-semibold text-accent text-sm">{Math.round(want.progress)}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-secondary/50 dark:bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-accent transition-all duration-500 ease-out" 
                            style={{ width: `${want.progress}%` }} 
                          />
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
              <div className="pt-2 flex justify-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowArchivedDialog(true)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-full hover:bg-secondary/60"
                  data-testid="view-archived-goals-btn"
                >
                  <Archive className="w-3.5 h-3.5" />
                  View Archived Goals
                </Button>
              </div>
            </div>
          </section>

          {/* ============ TO DO ============ */}
          <section className="space-y-4 sm:space-y-5 fade-in layer-do" data-testid="todo-column">
            <ColumnHeader
              title={t('layer.todo')}
              subtitle={t('layer.todo.subtitle')}
              fontClass="font-sans font-medium"
              onAdd={() => setShowTodoForm(v => !v)}
              addTestId="add-todo-btn"
              headerTestId="todo-column-header"
            />

            {showTodoForm && (
              <form onSubmit={createTodo} className="bg-secondary/30 rounded-xl p-4 sm:p-5 space-y-3 border border-border/60" data-testid="todo-form">
                <Input placeholder={t('todo.title.placeholder')} value={todoForm.title} onChange={(e) => setTodoForm({ ...todoForm, title: e.target.value })} required className="text-sm" data-testid="todo-title-input" />
                <Textarea placeholder={t('todo.description.placeholder')} value={todoForm.description} onChange={(e) => setTodoForm({ ...todoForm, description: e.target.value })} className="resize-none text-sm" rows={2} data-testid="todo-description-input" />
                <select value={todoForm.want_id} onChange={(e) => setTodoForm({ ...todoForm, want_id: e.target.value })} className="w-full p-2 rounded-lg bg-background border border-border text-xs" data-testid="todo-want-select">
                  <option value="">{t('todo.connect.want')}</option>
                  {wants.map(w => <option key={w.id} value={w.id}>{w.title}</option>)}
                </select>
                <select value={todoForm.being_id} onChange={(e) => setTodoForm({ ...todoForm, being_id: e.target.value })} className="w-full p-2 rounded-lg bg-background border border-border text-xs" data-testid="todo-being-select">
                  <option value="">{t('todo.connect.being')}</option>
                  {beings.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                </select>
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="rounded-full text-xs" data-testid="todo-submit-btn">{t('form.create')}</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowTodoForm(false)} data-testid="todo-cancel-btn">{t('form.cancel')}</Button>
                </div>
              </form>
            )}

            <div className="space-y-2.5 layer-column">
              {todos.length === 0 ? (
                <EmptyState icon={<ListChecks className="w-6 h-6" />} text={t('todo.empty')} />
              ) : (
                todos.map((todo) => (
                  <article
                    key={todo.id}
                    className={`card-do task-item rounded-lg p-3 sm:p-4 hover:shadow-sm transition-all duration-300 ${focusClass('todos', todo.id)}`}
                    onMouseEnter={() => setFocus({ todoId: todo.id })}
                    onMouseLeave={() => setFocus(null)}
                    data-testid={`todo-item-${todo.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleTodo(todo.id)} className="mt-0.5 text-accent hover:scale-110 transition-transform flex-shrink-0" data-testid={`todo-toggle-${todo.id}`}>
                        {todo.completed ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <p className={`font-medium text-sm ${todo.completed ? 'line-through text-muted-foreground' : ''}`}>{todo.title}</p>
                          {anchorByItemId[todo.id] && (
                            <span className="bg-accent/15 text-accent border border-accent/20 px-2 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1 font-sans font-semibold">
                              📅 {anchorByItemId[todo.id].time}
                            </span>
                          )}
                        </div>
                        {todo.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{todo.description}</p>
                        )}
                        {(todo.want_id || todo.being_id) && (
                          <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                            {todo.want_id && (
                              <span className="connection-line px-2 py-0.5 bg-accent/10 text-accent rounded-full">
                                → {wantById[todo.want_id]?.title}
                              </span>
                            )}
                            {todo.being_id && (
                              <span className="px-2 py-0.5 bg-primary/5 text-primary/80 rounded-full inline-flex items-center gap-1">
                                <span className="font-serif italic">⟡</span> {beingById[todo.being_id]?.title}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleDeleteTodoClick(todo.id)} className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0" data-testid={`todo-delete-${todo.id}`}>
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
        )}
      </div>

      {/* Right side: Sidebar stack */}
      {showSidebar && (
        <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-24">
          <WeeklyReviewCard
            token={token}
            onEngage={(prompt) => setPendingChatPrompt(prompt)}
            onDueChange={setIsReviewDue}
          />
          
          <StalledGoalsCard
            token={token}
            user={user}
            stalledGoals={stalledWants}
            onEngage={(prompt) => setPendingChatPrompt(prompt)}
            onCommitted={fetchData}
          />
          
          {(focusBeing || todayTodos.length > 0) && (
            <section className="today-strip rounded-2xl p-4 sm:p-6 mb-6 fade-in" data-testid="today-strip">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Sunrise className="w-4 h-4 text-accent" />
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t('today.label')}</span>
              </div>
              <div className="space-y-4">
                {focusBeing && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{t('today.embody')}</p>
                    <h3 className="font-sans text-base sm:text-lg font-semibold leading-snug">{focusBeing.title}</h3>
                    {focusBeing.type === 'habit' && (
                      <p className="streak-flame text-xs mt-1">
                        <Flame className="w-3.5 h-3.5 inline mr-1 text-orange-500" /> {focusBeing.streak || 0} {t('being.days')}
                      </p>
                    )}
                  </div>
                )}
                {focusBeing && todayTodos.length > 0 && <div className="border-t border-border/40 my-2" />}
                {todayTodos.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{t('today.next')}</p>
                    <ul className="space-y-2">
                      {todayTodos.map((td) => (
                        <li key={td.id} className="flex items-start gap-2 text-sm leading-tight">
                          <button onClick={() => toggleTodo(td.id)} className="text-accent hover:scale-110 transition-transform mt-0.5 flex-shrink-0" data-testid={`today-todo-toggle-${td.id}`}>
                            <Circle className="w-4 h-4" />
                          </button>
                          <div>
                            <span>{td.title}</span>
                            {td.want_id && (
                              <span className="text-xs text-muted-foreground block">→ {wantById[td.want_id]?.title}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  </div>

      <AIChat
        token={token}
        user={user}
        onCommitted={fetchData}
        pendingPrompt={pendingChatPrompt}
        onPendingConsumed={() => setPendingChatPrompt(null)}
      />
      <ArchivedGoalsDialog
        open={showArchivedDialog}
        onOpenChange={setShowArchivedDialog}
        token={token}
        onRestored={fetchData}
      />
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.type === 'being' && t('dialog.delete.being.title', 'Delete this Being?')}
              {deleteTarget?.type === 'want' && t('dialog.delete.want.title', 'Delete this Goal?')}
              {deleteTarget?.type === 'todo' && t('dialog.delete.todo.title', 'Delete this Task?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'being' && t('dialog.delete.being.desc', 'All connected goals and tasks will be removed along with this identity.')}
              {deleteTarget?.type === 'want' && t('dialog.delete.want.desc', 'All connected tasks will be deleted along with this goal.')}
              {deleteTarget?.type === 'todo' && t('dialog.delete.todo.desc', 'This task will be permanently deleted.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}>
              {t('dialog.delete.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              data-testid="confirm-delete-button"
            >
              {t('dialog.delete.confirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ColumnHeader({ title, subtitle, fontClass, onAdd, addTestId, headerTestId }) {
  return (
    <header className="space-y-2" data-testid={headerTestId}>
      <div className="flex items-center justify-between">
        <h2 className={`${fontClass} text-xl sm:text-2xl tracking-tight`}>{title}</h2>
        <Button
          onClick={onAdd}
          variant="ghost"
          size="sm"
          className="rounded-full h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          data-testid={addTestId}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {subtitle && <p className="text-xs text-muted-foreground uppercase tracking-[0.18em]">{subtitle}</p>}
      <div className="column-rule" />
    </header>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="text-center py-10 sm:py-14 text-muted-foreground">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/40 mb-3 text-muted-foreground/60">
        {icon}
      </div>
      <p className="text-sm font-serif italic max-w-[200px] mx-auto leading-relaxed">{text}</p>
    </div>
  );
}
